import { useCallback, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import type { Attachment, AttachmentUploadResult } from '@/types/approval';
import { approvalCreateUploadingAttachmentsAtom } from './atoms';
import type { ApprovalFormValues } from './types';

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

/**
 * AI 업로더 파일을 품의 첨부로 남김(레거시 동일) — 업로드 중엔 첨부 목록 임시 항목
 * (레거시 attachment-item.uploading), 성공하면 attachments 에 추가
 */
const useAiUploadAttachments = (onStart?: (file: File) => void) => {
  const { getValues, setValue } = useFormContext<ApprovalFormValues>();
  const setUploading = useSetAtom(approvalCreateUploadingAttachmentsAtom);
  const uploadingIdsRef = useRef(new Map<File, string>());
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  const onUploadStart = useCallback(
    (file: File) => {
      onStartRef.current?.(file);
      const id = `uploading-${Date.now()}-${uploadingIdsRef.current.size}`;
      uploadingIdsRef.current.set(file, id);
      setUploading((prev) => [...prev, { id, fileName: file.name }]);
    },
    [setUploading],
  );

  const onUploadSettled = useCallback(
    (file: File) => {
      const id = uploadingIdsRef.current.get(file);
      uploadingIdsRef.current.delete(file);
      if (id) setUploading((prev) => prev.filter((u) => u.id !== id));
    },
    [setUploading],
  );

  const onUploaded = useCallback(
    (result: AttachmentUploadResult, file: File) => {
      const fileName = result.fileName ?? file.name;
      const next: Attachment = {
        fileUuid: result.uuid,
        fileName,
        fileSize: result.fileSizeInBytes ?? file.size,
        fileFormat: getExt(fileName),
        uploadDate: result.createdAt,
        urlOrg: '',
      };
      setValue('attachments', [...getValues('attachments'), next], {
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  return { onUploadStart, onUploadSettled, onUploaded };
};

export default useAiUploadAttachments;
